import { useEffect, useState } from "react"
import { getCsrfToken } from "@/lib/csrf"
import { formatMoneyWhole } from "@/lib/billing-format"

const DEBOUNCE_MS = 200

export type GenerationPriceView = {
  status: "loading" | "ready" | "hidden"
  label: string | null
}

export type GenerationPriceInput = {
  kind: string
  projectId?: number | null
  params: Record<string, unknown>
  /** false — не запрашивать (например длительность «Авто»). */
  enabled?: boolean
}

type PriceResponse = {
  estimable?: boolean
  total_microcents?: number | null
  /** Валюта счёта списания проекта (docs/billing_currency_accounts.md §4.3). */
  currency?: string | null
}

const HIDDEN: GenerationPriceView = { status: "hidden", label: null }

function stableKey(kind: string, projectId: number | null, params: Record<string, unknown>): string {
  const sorted: Record<string, unknown> = {}
  for (const name of Object.keys(params).sort()) sorted[name] = params[name]
  return JSON.stringify({ kind, projectId, params: sorted })
}

function isAbort(error: unknown): boolean {
  return error instanceof DOMException && error.name === "AbortError"
}

async function postGenerationPrice(key: string, signal: AbortSignal): Promise<PriceResponse> {
  const parsed = JSON.parse(key) as { kind: string; projectId: number | null; params: Record<string, unknown> }
  const body: { kind: string; project_id?: number; params: Record<string, unknown> } = {
    kind: parsed.kind,
    params: parsed.params,
  }
  if (parsed.projectId != null) body.project_id = parsed.projectId

  const res = await fetch("/generation_price", {
    method: "POST",
    credentials: "same-origin",
    headers: {
      "Content-Type": "application/json",
      Accept: "application/json",
      "X-Requested-With": "XMLHttpRequest",
      "X-CSRF-Token": getCsrfToken(),
    },
    body: JSON.stringify(body),
    signal,
  })
  if (!res.ok) throw new Error(`HTTP ${res.status}`)
  return (await res.json()) as PriceResponse
}

function viewFromResponse(data: PriceResponse): GenerationPriceView {
  if (data.estimable && typeof data.total_microcents === "number") {
    return { status: "ready", label: formatMoneyWhole(data.total_microcents, data.currency ?? "RUB") }
  }
  return HIDDEN
}

/** Цена черновика: слот сразу в loading, запрос с debounce, устаревший ответ отбрасывается. */
export function useGenerationPrice(input: GenerationPriceInput): GenerationPriceView {
  const enabled = input.enabled !== false
  const key = enabled ? stableKey(input.kind, input.projectId ?? null, input.params) : null
  const [fetched, setFetched] = useState<{ key: string; view: GenerationPriceView } | null>(null)

  useEffect(() => {
    if (!key) return

    const controller = new AbortController()
    let cancelled = false
    const timer = window.setTimeout(() => {
      void postGenerationPrice(key, controller.signal)
        .then((data) => {
          if (cancelled) return
          setFetched({ key, view: viewFromResponse(data) })
        })
        .catch((error: unknown) => {
          if (cancelled || isAbort(error)) return
          setFetched({ key, view: HIDDEN })
        })
    }, DEBOUNCE_MS)

    return () => {
      cancelled = true
      window.clearTimeout(timer)
      controller.abort()
    }
  }, [key])

  if (!key) return HIDDEN
  if (fetched?.key === key) return fetched.view
  return { status: "loading", label: null }
}
